import type { Client } from '@pm/schemas';
import { displayDocument } from '@pm/schemas';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  SkeletonRows,
  Tabs,
} from '@pm/ui';
import { Building2, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useAuth } from '@/lib/auth';
import { fmtDate } from '@/lib/format';
import { useClients } from '@/lib/queries';
import { ClientDialog } from './ClientDialog';

export function ClientsPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<'active' | 'inactive' | 'trash'>('active');
  const [create, setCreate] = useState(false);
  const { data, isLoading, isError, error, refetch } = useClients({
    q: q || undefined,
    status: tab === 'inactive' ? 'INACTIVE' : tab === 'active' ? 'ACTIVE' : undefined,
    trashed: tab === 'trash' || undefined,
  });
  const items = data?.data ?? [];

  return (
    <div className="flex flex-col gap-gap-lg">
      <PageHeader
        title="Clientes"
        subtitle={
          <span className="font-mono text-xs text-text-3">{data?.meta.total ?? 0} clientes</span>
        }
        actions={
          can('clients.create') ? (
            <Button variant="primary" onClick={() => setCreate(true)}>
              <Plus /> Novo cliente
            </Button>
          ) : null
        }
      />
      <div className="flex flex-wrap items-center gap-gap">
        <Input
          leading={<Search />}
          placeholder="Buscar por nome ou documento"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-[280px]"
        />
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { value: 'active', label: 'Ativos' },
            { value: 'inactive', label: 'Inativos' },
            { value: 'trash', label: 'Lixeira' },
          ]}
          className="ml-auto border-0"
        />
      </div>
      <div className="overflow-x-auto rounded-md border border-border bg-surface-1">
        {isLoading ? (
          <SkeletonRows />
        ) : isError ? (
          <ErrorState message={error.message} onRetry={() => void refetch()} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={Building2}
            title={tab === 'trash' ? 'Lixeira vazia' : 'Nenhum cliente'}
            description={
              tab === 'active' && !q
                ? 'Cadastre o primeiro cliente para vincular demandas.'
                : undefined
            }
            action={
              tab === 'active' && !q && can('clients.create') ? (
                <Button variant="primary" size="sm" onClick={() => setCreate(true)}>
                  <Plus /> Novo cliente
                </Button>
              ) : undefined
            }
          />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-strong bg-bg font-mono text-xs uppercase tracking-[var(--tracking-caps)] text-text-3">
                <th className="h-8 px-3 text-left font-medium">Cliente</th>
                <th className="h-8 px-3 text-left font-medium">Tipo</th>
                <th className="h-8 px-3 text-left font-medium">Documento</th>
                <th className="h-8 px-3 text-left font-medium">E-mail</th>
                <th className="h-8 px-3 text-right font-medium">Contatos</th>
                <th className="h-8 px-3 text-right font-medium">Demandas abertas</th>
                <th className="h-8 px-3 text-left font-medium">Desde</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c: Client) => (
                <tr
                  key={c.id}
                  className="h-row cursor-pointer border-b border-border last:border-b-0 hover:bg-surface-2"
                  onClick={() => navigate(`/clientes/${c.id}`)}
                >
                  <td className="px-3">
                    <Link to={`/clientes/${c.id}`} className="font-medium hover:text-accent">
                      {c.trade_name || c.legal_name}
                    </Link>
                    {c.trade_name ? (
                      <span className="ml-2 text-sm text-text-3">{c.legal_name}</span>
                    ) : null}
                  </td>
                  <td className="px-3">
                    <Badge tone={c.person_type === 'COMPANY' ? 'info' : 'neutral'}>
                      {c.person_type === 'COMPANY' ? 'PJ' : 'PF'}
                    </Badge>
                  </td>
                  <td className="px-3 font-mono text-sm">
                    {c.document ? displayDocument(c.document, c.person_type) : '—'}
                  </td>
                  <td className="px-3 font-mono text-sm text-text-2">{c.email ?? '—'}</td>
                  <td className="px-3 text-right font-mono text-sm">{c.contacts_count ?? 0}</td>
                  <td className="px-3 text-right font-mono text-sm">{c.open_tasks ?? 0}</td>
                  <td className="px-3 font-mono text-xs text-text-3">{fmtDate(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <ClientDialog
        open={create}
        onOpenChange={setCreate}
        onSaved={(c) => navigate(`/clientes/${c.id}`)}
      />
    </div>
  );
}
